import { Controller } from "@hotwired/stimulus";
import Decimal from "decimal.js";

// Preview only: the server remains authoritative for every saved adjustment.
export default class extends Controller<HTMLFormElement> {
  static targets = ["amount", "adjustment", "preview"];
  static values = { calculated: String, currency: String };

  declare readonly amountTarget: HTMLInputElement;
  declare readonly adjustmentTarget: HTMLOutputElement;
  declare readonly previewTarget: HTMLElement;
  declare readonly calculatedValue: string;
  declare readonly currencyValue: string;

  connect(): void {
    this.update();
  }

  update(): void {
    this.previewTarget.hidden = false;
    const raw = this.amountTarget.value;
    if (!this.amountTarget.validity.valid || raw.trim() === "") {
      this.adjustmentTarget.textContent = "Enter a bank balance to preview";
      return;
    }

    try {
      const bank = new Decimal(raw);
      const calculated = new Decimal(this.calculatedValue);
      if (!bank.isFinite() || !calculated.isFinite()) throw new Error("Non-finite balance");
      // Keep low-order digits even when subtracting very large balances.
      const Exact = Decimal.clone({
        precision: Math.max(bank.e, calculated.e, 0) -
          Math.min(bank.e - bank.sd(), calculated.e - calculated.sd(), 0) + 4,
      });
      const difference = new Exact(raw).minus(this.calculatedValue);
      this.adjustmentTarget.textContent = difference.isZero()
        ? `0.00 ${this.currencyValue} — no change to balance`
        : `${difference.isPositive() ? "+" : ""}${difference.toFixed(Math.max(2, difference.decimalPlaces()))} ${this.currencyValue}`;
    } catch {
      this.adjustmentTarget.textContent = "Adjustment preview unavailable";
    }
  }
}
